import { motion } from "framer-motion";
import { type FC, useState } from "react";

import { formatAbbrevNumber, formatUsd } from "../../utils/currency";
import { SIGNAL_HEX } from "../common/colorTokens";
import { Sparkline } from "../common/Sparkline";
import { Text } from "../common/Text";
import { type BlackboardInstance } from "./blackboardModels";
import { BurnRateModal } from "./BurnRateModal";
import { useFleetActivitySeries } from "./useFleetActivitySeries";
import { WakeGrowthModal } from "./WakeGrowthModal";

interface FleetBentoHeaderProps {
  instances: BlackboardInstance[];
  generatingInstances: ReadonlySet<string>;
}

export const FleetBentoHeader: FC<FleetBentoHeaderProps> = ({
  instances,
  generatingInstances,
}) => {
  const {
    burnRateUsdPerHr,
    burnRateSamples,
    tokensPerMinSamples,
    cacheHitSamples,
    growthPerMin,
    growthPerMinSamples,
  } = useFleetActivitySeries();
  const [modalOpen, setModalOpen] = useState(false);
  const [burnHovered, setBurnHovered] = useState(false);
  const [growthModalOpen, setGrowthModalOpen] = useState(false);
  const [activityHovered, setActivityHovered] = useState(false);

  // A "parked" actor that's currently generating is effectively running from
  // The operator's view — the REST status field only flips on park/wake events,
  // So it can lag mid-activation.
  const runningCount = instances.filter(
    (i) => i.status === "running" || generatingInstances.has(i.id),
  ).length;
  const waitingCount = instances.filter(
    (i) => i.status === "waiting" && !generatingInstances.has(i.id),
  ).length;
  const visibleFleetCount = runningCount + waitingCount;
  const total = Math.max(visibleFleetCount, 1);
  const generatingCount = generatingInstances.size;

  const currentTokens =
    tokensPerMinSamples[tokensPerMinSamples.length - 1] ?? 0;
  const currentCacheHit = cacheHitSamples[cacheHitSamples.length - 1] ?? 0;

  // Growth rate color: thresholds anchored to fleet wake-growth distribution
  // Measured 2026-04-25 (mean ~8k/wake; ~1-2 wakes/min during active periods
  // → mean fleet growth ≈ 10-20k/min). >50k/min = unusual, >100k/min = spike.
  const growthColor = pickGrowthColor(growthPerMin);

  const burnColor = (() => {
    if (burnRateUsdPerHr === null) {
      return SIGNAL_HEX.neutral;
    }
    if (burnRateUsdPerHr > 5) {
      return SIGNAL_HEX.danger;
    }
    if (burnRateUsdPerHr > 1) {
      return SIGNAL_HEX.warning;
    }
    return SIGNAL_HEX.healthy;
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
    >
      {/* 1. Burn-rate hero (spans 2 cols on large) */}
      <div
        className="group relative cursor-pointer overflow-hidden border border-slate-200 bg-white p-4 transition-colors hover:border-emerald-400/50 lg:col-span-2 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-emerald-400/40"
        onMouseEnter={() => setBurnHovered(true)}
        onMouseLeave={() => setBurnHovered(false)}
        onClick={() => setModalOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setModalOpen(true);
          }
        }}
        aria-label="Expand burn rate history"
      >
        <div
          className="absolute inset-x-0 top-0 h-0.5"
          style={{ backgroundColor: burnColor }}
        />
        <motion.button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setModalOpen(true);
          }}
          className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center border border-slate-300 bg-white/80 text-slate-500 backdrop-blur hover:border-emerald-400 hover:text-emerald-500 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-400 dark:hover:border-emerald-400/60 dark:hover:text-emerald-300"
          initial={false}
          animate={{
            opacity: burnHovered ? 1 : 0,
            scale: burnHovered ? 1 : 0.9,
          }}
          transition={{ duration: 0.15 }}
          aria-label="Open spend history"
        >
          <ExpandIcon />
        </motion.button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Text
              as="p"
              variant="label"
              className="text-slate-400 dark:text-slate-500"
            >
              [ BURN RATE ]
            </Text>
            <div className="mt-1 flex items-baseline gap-1.5">
              {/* Idle fleet reads as calm, not broken: "quiet", never a dash. */}
              <span
                className={`font-mono text-2xl font-semibold tabular-nums tracking-tight ${
                  burnRateUsdPerHr === null
                    ? "text-slate-400 dark:text-slate-600"
                    : "text-slate-900 dark:text-white"
                }`}
                style={{
                  color: burnRateUsdPerHr === null ? undefined : burnColor,
                }}
              >
                {burnRateUsdPerHr === null
                  ? "quiet"
                  : formatUsd(burnRateUsdPerHr)}
              </span>
              {burnRateUsdPerHr !== null && (
                <Text
                  variant="label"
                  className="text-slate-400 dark:text-slate-500"
                >
                  /hr
                </Text>
              )}
            </div>
            <p className="mt-0.5 font-mono text-[10px] tracking-tight text-slate-400 dark:text-slate-500">
              fleet spend — 60s window ·{" "}
              <span className="text-emerald-500/80 opacity-0 transition-opacity group-hover:opacity-100 dark:text-emerald-400/80">
                click to view history
              </span>
            </p>
          </div>
          <Sparkline
            values={burnRateSamples}
            color={burnColor}
            width={140}
            height={44}
          />
        </div>
      </div>
      <BurnRateModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* 2. Fleet activity — tokens/min + cache hit sparklines. Clicking opens
          the per-actor wake-growth distribution modal. */}
      <div
        aria-label="Open wake growth distribution"
        className="group relative cursor-pointer overflow-hidden border border-slate-200 bg-white p-4 transition-colors hover:border-sky-400/50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-sky-400/40"
        onClick={() => setGrowthModalOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setGrowthModalOpen(true);
          }
        }}
        onMouseEnter={() => setActivityHovered(true)}
        onMouseLeave={() => setActivityHovered(false)}
        role="button"
        tabIndex={0}
      >
        <div className="absolute inset-x-0 top-0 h-0.5 bg-sky-500" />
        <motion.button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setGrowthModalOpen(true);
          }}
          className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center border border-slate-300 bg-white/80 text-slate-500 backdrop-blur hover:border-sky-400 hover:text-sky-500 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-400 dark:hover:border-sky-400/60 dark:hover:text-sky-300"
          initial={false}
          animate={{
            opacity: activityHovered ? 1 : 0,
            scale: activityHovered ? 1 : 0.9,
          }}
          transition={{ duration: 0.15 }}
          aria-label="Open wake growth distribution"
        >
          <ExpandIcon />
        </motion.button>
        <Text
          as="p"
          variant="label"
          className="text-slate-400 dark:text-slate-500"
        >
          [ FLEET ACTIVITY ]
        </Text>
        <div className="mt-2 space-y-2">
          <ActivityRow
            label="fleet tok/min"
            value={formatAbbrevNumber(currentTokens, "round")}
            values={tokensPerMinSamples}
            color={SIGNAL_HEX.info}
          />
          <ActivityRow
            label="cache %"
            value={
              cacheHitSamples.length === 0
                ? "no wakes"
                : `${Math.round(currentCacheHit)}%`
            }
            values={cacheHitSamples}
            color={SIGNAL_HEX.healthy}
          />
          <ActivityRow
            label="ctx grow"
            value={
              growthPerMin === null
                ? "flat"
                : `${formatAbbrevNumber(growthPerMin, "round")}/min`
            }
            values={growthPerMinSamples}
            color={growthColor}
          />
        </div>
        <p className="mt-2 font-mono text-[10px] tracking-tight text-sky-500/80 opacity-0 transition-opacity group-hover:opacity-100 dark:text-sky-400/80">
          click for per-actor distribution
        </p>
      </div>
      <WakeGrowthModal
        open={growthModalOpen}
        onClose={() => setGrowthModalOpen(false)}
      />

      {/* 3. Status breakdown bar */}
      <div className="relative overflow-hidden border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="absolute inset-x-0 top-0 h-0.5 bg-violet-500" />
        <div className="flex items-baseline justify-between">
          <Text
            as="p"
            variant="label"
            className="text-slate-400 dark:text-slate-500"
          >
            [ LIVE FLEET ]
          </Text>
          <p className="font-mono text-[10px] tracking-tight text-slate-400 dark:text-slate-500">
            {visibleFleetCount} visible
          </p>
        </div>
        <div className="mt-3 flex h-2 w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
          <motion.div
            className="bg-emerald-500"
            initial={{ width: 0 }}
            animate={{ width: `${(runningCount / total) * 100}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
          <motion.div
            className="bg-amber-400"
            initial={{ width: 0 }}
            animate={{ width: `${(waitingCount / total) * 100}%` }}
            transition={{ delay: 0.05, duration: 0.4, ease: "easeOut" }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between font-mono text-[10px] tabular-nums">
          <LegendDot
            color="bg-emerald-500"
            label="running"
            value={runningCount}
          />
          <LegendDot color="bg-amber-400" label="parked" value={waitingCount} />
        </div>
        {generatingCount > 0 && (
          <div className="mt-2 flex items-center gap-1.5 font-mono text-[10px] text-emerald-600 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 animate-ping rounded-full bg-emerald-500" />
            {generatingCount} generating
          </div>
        )}
      </div>
    </motion.div>
  );
};

const ActivityRow: FC<{
  label: string;
  value: string;
  values: number[];
  color: string;
}> = ({ label, value, values, color }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-2">
      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500">
        {label}
      </span>
      <span
        className="font-mono text-xs font-semibold tabular-nums text-slate-700 dark:text-slate-200"
        style={{ color }}
      >
        {value}
      </span>
    </div>
    <Sparkline values={values} color={color} width={90} height={20} />
  </div>
);

const LegendDot: FC<{ color: string; label: string; value: number }> = ({
  color,
  label,
  value,
}) => (
  <span className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
    <span className={`h-1.5 w-1.5 ${color}`} />
    <span className="uppercase tracking-widest">{label}</span>
    <span className="text-slate-700 dark:text-slate-200">{value}</span>
  </span>
);

const ExpandIcon: FC = () => (
  <svg
    width={12}
    height={12}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="square"
    aria-hidden="true"
  >
    <path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" />
  </svg>
);

function pickGrowthColor(growthPerMin: number | null): string {
  if (growthPerMin === null) {
    return SIGNAL_HEX.neutral;
  }
  if (growthPerMin > 100_000) {
    return SIGNAL_HEX.danger;
  }
  if (growthPerMin > 50_000) {
    return SIGNAL_HEX.warning;
  }
  return SIGNAL_HEX.accentGrowth;
}
